/**
 * kd-algolia-debug-counter: Debug overlay for counting Algolia API requests
 * Replaces assets/algolia-request-counter.js
 * Activated via ?debug URL parameter.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

interface RequestEntry {
  timestamp: number;
  url: string;
  method: string;
  subQueries: number;
  source: string;
}

@customElement('kd-algolia-debug-counter')
export class KdAlgoliaDebugCounter extends LitElement {
  createRenderRoot() { return this; }

  @state() private _total = 0;

  @state() private _cacheHits = 0;

  @state() private _cacheMisses = 0;

  @state() private _active = false;

  // Dragging state
  private _isDragging = false;

  private _offsetX = 0;

  private _offsetY = 0;

  private _overlayEl: HTMLElement | null = null;

  private _log: RequestEntry[] = [];

  private _originalFetch: typeof window.fetch | null = null;

  private _originalXhrOpen: typeof XMLHttpRequest.prototype.open | null = null;

  private _originalXhrSend: typeof XMLHttpRequest.prototype.send | null = null;

  private _proxyPort: string | null = null;

  private _cacheHitHandler = () => this._recordCacheHit();

  private _mouseMoveHandler = (e: MouseEvent) => this._onMouseMove(e);

  private _mouseUpHandler = () => { this._isDragging = false; };

  connectedCallback(): void {
    super.connectedCallback();

    const params = new URLSearchParams(window.location.search);
    if (!params.has('debug')) return;

    this._active = true;
    this._proxyPort = params.get('proxy') || null;

    this._interceptFetch();
    this._interceptXhr();

    document.addEventListener('algolia-cache-hit', this._cacheHitHandler);
    document.addEventListener('mousemove', this._mouseMoveHandler);
    document.addEventListener('mouseup', this._mouseUpHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._restoreInterceptors();
    document.removeEventListener('algolia-cache-hit', this._cacheHitHandler);
    document.removeEventListener('mousemove', this._mouseMoveHandler);
    document.removeEventListener('mouseup', this._mouseUpHandler);
  }

  private _isAlgoliaUrl(url: string): boolean {
    return url.indexOf('algolia.net') !== -1 || url.indexOf('algolianet.com') !== -1;
  }

  private _rewriteToProxy(url: string): string {
    if (!this._proxyPort || !this._isAlgoliaUrl(url)) return url;
    try {
      const parsed = new URL(url);
      return `http://127.0.0.1:${this._proxyPort}${parsed.pathname}${parsed.search}`;
    } catch {
      return url;
    }
  }

  private _parseSubQueryCount(body: unknown): number {
    if (!body || typeof body !== 'string') return 1;
    try {
      const parsed = JSON.parse(body);
      if (parsed?.requests && Array.isArray(parsed.requests)) {
        return parsed.requests.length;
      }
    } catch { /* not JSON */ }
    return 1;
  }

  private _recordRequest(url: string, method: string, body: unknown, source: string): void {
    this._log.push({
      timestamp: Date.now(),
      url,
      method,
      subQueries: this._parseSubQueryCount(body),
      source,
    });
    this._total++;
    this._cacheMisses++;
  }

  private _recordCacheHit(): void {
    this._cacheHits++;
  }

  private _interceptFetch(): void {
    this._originalFetch = window.fetch;
    // アロー関数で this バインドを回避（lint: @typescript-eslint/no-this-alias）
    window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      let url: string;
      if (typeof input === 'string') url = input;
      else if (input instanceof URL) url = input.toString();
      else url = input.url || '';

      if (this._isAlgoliaUrl(url)) {
        const body = init?.body ?? '';
        this._recordRequest(url, init?.method || 'POST', body, 'fetch');
        const proxiedUrl = this._rewriteToProxy(url);
        if (proxiedUrl !== url) {
          if (typeof input === 'string') {
            return this._originalFetch!(proxiedUrl, init);
          }
          return this._originalFetch!(new Request(proxiedUrl, input as Request), init);
        }
      }
      return this._originalFetch!(input, init);
    };
  }

  private _interceptXhr(): void {
    this._originalXhrOpen = XMLHttpRequest.prototype.open;
    this._originalXhrSend = XMLHttpRequest.prototype.send;
    // prototype.open/send は XHR インスタンスの this 参照が必須のため function 宣言が必要。
    // アウターの this（counter インスタンス）は self に退避する。
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest & { __algolia_url__?: string; __algolia_method__?: string },
      method: string,
      url: string | URL,
      ...rest: unknown[]
    ) {
      const urlStr = url.toString();
      this.__algolia_url__ = urlStr;
      this.__algolia_method__ = method;
      const proxiedUrl = self._rewriteToProxy(urlStr);
      return self._originalXhrOpen!.call(this, method, proxiedUrl, ...(rest as [boolean?, string?, string?]));
    };

    XMLHttpRequest.prototype.send = function (
      this: XMLHttpRequest & { __algolia_url__?: string; __algolia_method__?: string },
      body?: Document | XMLHttpRequestBodyInit | null,
    ) {
      if (this.__algolia_url__ && self._isAlgoliaUrl(this.__algolia_url__)) {
        self._recordRequest(this.__algolia_url__, this.__algolia_method__ || 'POST', body, 'xhr');
      }
      return self._originalXhrSend!.call(this, body);
    };
  }

  private _restoreInterceptors(): void {
    if (this._originalFetch) {
      window.fetch = this._originalFetch;
    }
    if (this._originalXhrOpen) {
      XMLHttpRequest.prototype.open = this._originalXhrOpen;
    }
    if (this._originalXhrSend) {
      XMLHttpRequest.prototype.send = this._originalXhrSend;
    }
  }

  private _reset(): void {
    this._log.length = 0;
    this._total = 0;
    this._cacheHits = 0;
    this._cacheMisses = 0;
  }

  private _onMouseDown(e: MouseEvent): void {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    this._isDragging = true;
    this._overlayEl = (e.currentTarget as HTMLElement);
    const rect = this._overlayEl.getBoundingClientRect();
    this._offsetX = e.clientX - rect.left;
    this._offsetY = e.clientY - rect.top;
  }

  private _onMouseMove(e: MouseEvent): void {
    if (!this._isDragging || !this._overlayEl) return;
    this._overlayEl.style.left = `${e.clientX - this._offsetX}px`;
    this._overlayEl.style.top = `${e.clientY - this._offsetY}px`;
    this._overlayEl.style.right = 'auto';
    this._overlayEl.style.bottom = 'auto';
  }

  private _getTotalSubQueries(): number {
    return this._log.reduce((sum, e) => sum + e.subQueries, 0);
  }

  render() {
    if (!this._active) return nothing;

    return html`
      <div
        class="fixed bottom-4 right-4 z-[99999] bg-[#1a1a2e] text-gray-200 px-5 py-4 rounded-lg font-mono text-sm shadow-lg min-w-[260px] cursor-move select-none"
        @mousedown=${this._onMouseDown}
      >
        <div class="flex justify-between items-center mb-3">
          <strong class="text-indigo-400 text-base">Algolia Counter</strong>
          <button
            class="bg-gray-700 text-white border-none rounded px-3 py-1 cursor-pointer text-xs"
            @click=${this._reset}
          >Reset</button>
        </div>
        <div class="flex flex-col gap-1.5">
          <div class="flex justify-between">
            <span class="text-gray-400">HTTP Requests</span>
            <span class="text-red-400 font-bold">${this._total}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-400">Sub-queries</span>
            <span>${this._getTotalSubQueries()}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-400">Cache HITs</span>
            <span class="text-green-400 font-bold">${this._cacheHits}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-400">Cache MISSes</span>
            <span class="text-orange-300 font-bold">${this._cacheMisses}</span>
          </div>
        </div>
      </div>
    `;
  }
}
